// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState } from 'react';
import type { ValidationResult } from '../../../lib/outputValidation';

interface ValidationBannerProps {
  validation: {
    overall: ValidationResult;
    qaly: ValidationResult;
    budgetImpact: ValidationResult;
  } | null;
}

/**
 * Validation banner component - displays non-blocking warnings/errors for validation issues
 * 
 * Shows a simple alert-style box at the top of the dashboard when validation issues are detected.
 * Groups issues by field and shows only the most severe issue per field to limit spam.
 */
export const ValidationBanner: React.FC<ValidationBannerProps> = ({ validation }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Guard early - render nothing if no validation or no issues, or if dismissed
  if (!validation || validation.overall.severity === 'ok' || isDismissed) {
    return null;
  }

  // Get unique fields with issues and their most severe issue
  const fieldIssues = new Map<string, { severity: 'warning' | 'error'; message: string }>();
  
  for (const issue of validation.overall.issues) {
    // Skip 'ok' severity issues (shouldn't happen, but type-safe)
    if (issue.severity === 'ok') continue;
    
    const existing = fieldIssues.get(issue.field);
    // Keep only the most severe issue per field
    if (!existing || (issue.severity === 'error' && existing.severity === 'warning')) {
      fieldIssues.set(issue.field, {
        severity: issue.severity as 'warning' | 'error', // Type assertion safe because we filtered 'ok'
        message: issue.message,
      });
    }
  }

  const fieldsWithIssues = Array.from(fieldIssues.keys());
  
  // Map field names to user-friendly labels
  const fieldLabels: Record<string, string> = {
    qaly: 'QALYs',
    budgetImpact: 'Budget impact',
    cost: 'Costs',
    other: 'Other metrics',
  };

  const friendlyFields = fieldsWithIssues.map(field => fieldLabels[field] || field).join(', ');
  
  const isError = validation.overall.severity === 'error';
  const backgroundColor = isError ? '#fee2e2' : '#fef3c7'; // red-100 : yellow-100
  const borderColor = isError ? '#ef4444' : '#f59e0b'; // red-500 : amber-500
  const textColor = isError ? '#991b1b' : '#92400e'; // red-900 : amber-900
  const iconColor = isError ? '#dc2626' : '#d97706'; // red-600 : amber-600

  return (
    <div
      style={{
        backgroundColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '12px 16px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        position: 'relative',
      }}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <div style={{ flexShrink: 0, marginTop: '2px' }}>
        {isError ? (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: iconColor }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM8.70711 7.29289C8.31658 6.90237 7.68342 6.90237 7.29289 7.29289C6.90237 7.68342 6.90237 8.31658 7.29289 8.70711L8.58579 10L7.29289 11.2929C6.90237 11.6834 6.90237 12.3166 7.29289 12.7071C7.68342 13.0976 8.31658 13.0976 8.70711 12.7071L10 11.4142L11.2929 12.7071C11.6834 13.0976 12.3166 13.0976 12.7071 12.7071C13.0976 12.3166 13.0976 11.6834 12.7071 11.2929L11.4142 10L12.7071 8.70711C13.0976 8.31658 13.0976 7.68342 12.7071 7.29289C11.6834 6.90237 11.6834 6.90237 11.2929 7.29289L10 8.58579L8.70711 7.29289Z"
              fill="currentColor"
            />
          </svg>
        ) : (
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ color: iconColor }}
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM10 9C10.5523 9 11 9.44772 11 10V13C11 13.5523 10.5523 14 10 14C9.44772 14 9 13.5523 9 13V10C9 9.44772 9.44772 9 10 9Z"
              fill="currentColor"
            />
          </svg>
        )}
      </div>

      {/* Dismiss Button */}
      <button
        onClick={() => setIsDismissed(true)}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: textColor,
          opacity: 0.7,
          borderRadius: '4px',
          transition: 'opacity 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '0.7';
        }}
        aria-label="Dismiss warning"
        title="Dismiss warning"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0, paddingRight: '24px' }}>
        <div
          style={{
            fontWeight: '600',
            fontSize: '14px',
            color: textColor,
            marginBottom: '6px',
          }}
        >
          {isError ? 'These results may be invalid.' : 'Results validation warning'}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: textColor,
            lineHeight: '1.5',
          }}
        >
          <div style={{ marginBottom: '6px' }}>
            Issues detected in: {friendlyFields}
          </div>
          {/* Show reasons for each issue */}
          {Array.from(fieldIssues.entries()).length > 0 && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: `1px solid ${borderColor}40` }}>
              {Array.from(fieldIssues.entries()).map(([field, issue]) => {
                const fieldLabel = fieldLabels[field] || field;
                return (
                  <div key={field} style={{ marginBottom: '4px', fontSize: '12px' }}>
                    <strong>{fieldLabel}:</strong> {issue.message}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

